import * as React from 'react'
import { times } from 'ramda'
import { bind } from 'classnames/bind'
import * as styles from '@procore/core-css/src/components/_pagination.scss'

import addSubcomponents from '../utils/addSubcomponents'
import {
  Props,
  PlacementNS,
  SizeNS,
  DivAttributes,
  ButtonAttributes,
  SpanAttributes,
} from '../utils/types'
import { OverlayTriggerVariantNS } from '../state/OverlayTrigger'
import Box, { BoxDisplayNS, FlexAlignNS, FlexJustifyNS } from '../layouts/Box'
import Flex from '../layouts/Flex'
import Button, { ButtonVariantNS } from './Button'
import Card from './Card'
import Font, { FontVariantNS, FontWeightNS } from './Font'
import Icon from './Icon'
import Menu, {
  Footer as MenuFooter,
  Selection,
  MenuConsumer,
  CSSMenuOptions,
  MenuApi,
} from './Menu'
import TextNotation, { NotationVariantNS } from './Notation'
import { OverlayTriggerAdapter as OverlayTrigger } from './OverlayTriggerAdapter'
import { Option as SelectOption } from './Select'

const cx = bind(styles)

interface FieldProps extends Props {
  label: string
}

interface PageNotationProps extends Props {
  start: number
  end: number
  items: number
}

export interface NavigationIconProps extends Props {
  onClick?: any
  disabled?: boolean
}

interface PresetPaginationProps extends Props {
  activePage: number
  fieldLabel?: string
  items: number
  perPage: number
  onSelectPage: (page: number) => void
}

interface PaginationMenuOptionsProps {
  pages: number
  menu: MenuApi
  children: any
}

interface PageSelectProps extends Props {
  disabled?: boolean
  pages?: number
  onSelect?: (selection: Selection) => any
  activePage?: number
}

const usePagination = ({
  perPage = 0,
  activePage = 0,
  items = 0,
  onSelectPage = (page: number): void => {},
}) => {
  const pages = Math.ceil(items / perPage)
  return {
    end: Math.min(activePage * perPage, items),
    isLast: activePage === pages,
    isFirst: activePage === 1,
    selectNext: () => onSelectPage(activePage + 1),
    selectPage: (selection: Selection) => onSelectPage(selection.item),
    selectPrev: () => onSelectPage(activePage - 1),
    start: (activePage - 1) * perPage + 1,
    pages,
  }
}

export const Container = React.forwardRef<
  HTMLDivElement,
  DivAttributes & Props
>((props, ref) => <Flex ref={ref} {...props} alignItems={FlexAlignNS.center} />)

export const Footer = React.forwardRef<HTMLDivElement, Props>((props, ref) => (
  <MenuFooter ref={ref} {...props} padding="xs 0 sm 0" />
))

export const Notation = React.forwardRef<HTMLDivElement, PageNotationProps>(
  ({ start, end, items }, ref) => (
    <TextNotation ref={ref} variant={NotationVariantNS.pagination}>
      {`${start}-${end} of ${items}`}
    </TextNotation>
  )
)

export const Next = React.forwardRef<
  HTMLButtonElement,
  ButtonAttributes & NavigationIconProps
>(({ className, disabled, ...props }, ref) => (
  <button
    ref={ref}
    {...props}
    disabled={disabled}
    className={cx('core-pagination__button', className)}
  >
    <Icon icon="chevron-right" />
  </button>
))

export const Prev = React.forwardRef<HTMLButtonElement, NavigationIconProps>(
  ({ className, disabled, ...props }, ref) => (
    <button
      ref={ref}
      {...props}
      disabled={disabled}
      className={cx('core-pagination__button', className)}
    >
      <Icon icon="chevron-left" />
    </button>
  )
)

const PaginationMenuOptions = ({
  pages,
  menu,
  children,
}: PaginationMenuOptionsProps) => {
  React.useEffect(() => {
    menu.setSize(pages)
  }, [pages])

  const childs = React.Children.toArray(children)

  return pages <= 10 ? (
    <CSSMenuOptions scrollable>{childs}</CSSMenuOptions>
  ) : (
    <React.Fragment>
      <CSSMenuOptions scrollable>{childs.slice(0, -1)}</CSSMenuOptions>
      <MenuFooter padding="xs none">{childs[childs.length - 1]}</MenuFooter>
    </React.Fragment>
  )
}

export const Select = React.forwardRef<HTMLButtonElement, PageSelectProps>(
  ({ disabled, pages = 0, onSelect, activePage = 1, ...rest }, ref) => (
    <OverlayTrigger
      autoFocus
      container={Card}
      containerClass={cx('core-pagination__overlay')}
      overlay={
        <Menu initialIndex={activePage - 1} onSelect={onSelect} scrollable>
          <MenuConsumer>
            {(menu: MenuApi) => {
              return (
                <PaginationMenuOptions pages={pages} menu={menu}>
                  {times((index: number) => {
                    const page = index + 1
                    return (
                      <SelectOption
                        selected={page === activePage}
                        value={page}
                        key={index}
                        hover={menu.index === index}
                        ref={menu.index === index ? menu.hoverRef : undefined}
                      >
                        {page}
                      </SelectOption>
                    )
                  }, pages)}
                </PaginationMenuOptions>
              )
            }}
          </MenuConsumer>
        </Menu>
      }
      placement={PlacementNS.bottomLeft}
      trigger={OverlayTriggerVariantNS.click}
      {...rest}
    >
      <Button
        disabled={disabled}
        dropdown
        ref={ref}
        variant={ButtonVariantNS.dropdownSecondary}
      >
        {activePage}
      </Button>
    </OverlayTrigger>
  )
)

export const Label = React.forwardRef<HTMLSpanElement, SpanAttributes & Props>(
  (props, ref) => (
    <Font
      ref={ref}
      {...props}
      size={SizeNS.sm}
      variant={FontVariantNS.primary}
      weight={FontWeightNS.regular}
    />
  )
)

export const Field = React.forwardRef<
  HTMLDivElement,
  DivAttributes & FieldProps
>(({ children, label }, ref) => (
  <Box ref={ref} display={BoxDisplayNS.flexRow} alignItems={FlexAlignNS.center}>
    <Box paddingRight={SizeNS.sm}>
      <Label>{`${label}:`}</Label>
    </Box>
    {children}
  </Box>
))

export const SelectField = React.forwardRef<HTMLDivElement, FieldProps>(
  ({ label, ...rest }, ref) => (
    <Field label={label}>
      <Select {...rest} />
    </Field>
  )
)

export const Pagination = React.forwardRef<
  HTMLDivElement,
  PresetPaginationProps
>(({ activePage, fieldLabel = 'Page', items, onSelectPage, perPage }, ref) => {
  const {
    end,
    isFirst,
    isLast,
    pages,
    selectNext,
    selectPage,
    selectPrev,
    start,
  } = usePagination({
    activePage,
    items,
    onSelectPage,
    perPage,
  })
  return (
    <Container ref={ref}>
      <Flex marginRight={SizeNS.xl}>
        <Notation start={start} end={end} items={items} />
      </Flex>

      <Field label={fieldLabel}>
        <Select
          activePage={activePage}
          disabled={pages <= 1}
          onSelect={selectPage}
          pages={pages}
        />
      </Field>

      <Flex
        alignItems={FlexAlignNS.none}
        justifyContent={FlexJustifyNS.none}
        marginLeft={SizeNS.lg}
      >
        <Prev onClick={selectPrev} disabled={isFirst} />
      </Flex>

      <Flex
        alignItems={FlexAlignNS.none}
        justifyContent={FlexJustifyNS.none}
        marginLeft={SizeNS.sm}
      >
        <Next onClick={selectNext} disabled={isLast} />
      </Flex>
    </Container>
  )
})

Pagination.displayName = 'Pagination'

Container.displayName = 'Pagination.Container'

Field.displayName = 'Pagination.Field'

Footer.displayName = 'Pagination.Footer'

Label.displayName = 'Pagination.Label'

Next.displayName = 'Pagination.Next'

Notation.displayName = 'Pagination.Notation'

Prev.displayName = 'Pagination.Prev'

SelectField.displayName = 'Pagination.SelectField'

export default addSubcomponents(
  {
    Container,
    Field,
    Footer,
    Label,
    Next,
    Notation,
    Prev,
    SelectField,
  },
  Pagination
)
